import { createElement, FC, RefObject } from "react";
import styles from "./TextInputGroup.module.scss";
import classNames from "classnames";
import { sanitizeInput, containsDangerousContent } from "../../utils/sanitize";

interface ITextInputGroupProps {
  placeholder: string;
  label: string;
  name: string;
  type: "text" | "date";
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled: boolean;
  error?: string | false;
  icon?: React.ElementType;
  isFocused?: boolean;
  setIsFocused: (e: boolean) => void;
  inputRef?: RefObject<HTMLInputElement | null>;
}

export const TextInputGroup: FC<ITextInputGroupProps> = ({
  placeholder,
  label,
  name,
  type,
  value,
  onChange,
  onBlur,
  disabled,
  error,
  icon,
  isFocused,
  setIsFocused,
  inputRef,
}) => {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    
    if (containsDangerousContent(inputValue)) {
      console.warn('Potentially dangerous content detected and sanitized');
      e.target.value = sanitizeInput(inputValue);
    }
    
    onChange(e);
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pastedText = e.clipboardData.getData('text');
    
    if (containsDangerousContent(pastedText)) {
      e.preventDefault();
      const sanitizedText = sanitizeInput(pastedText);
      const target = e.target as HTMLInputElement;
      target.value = sanitizedText;
      
      const changeEvent = new Event('input', { bubbles: true });
      target.dispatchEvent(changeEvent);
      
      console.warn('Potentially dangerous content detected in paste and sanitized');
    }
  };

  return (
    <div
      className={classNames(styles.formGroup, {
        [styles.focused]: isFocused,
        [styles.error]: !!error,
        [styles.disabled]: disabled,
      })}
    >
      <div className={styles.inputContainer}>
        <div className={styles.icon}>
          {icon && createElement(icon, { size: "1.2rem", stroke: 1.5 })}
        </div>
        <div
          className={classNames(styles.label, { [styles.focused]: isFocused })}
        >
          {label}
        </div>
        <input
          spellCheck="false"
          id={name}
          type={type}
          placeholder={placeholder}
          name={name}
          value={value}
          onChange={handleChange}
          onPaste={handlePaste}
          onBlur={(e) => {
            onBlur(e);
            setIsFocused(false);
          }}
          onFocus={() => setIsFocused(true)}
          disabled={disabled}
          ref={inputRef}
        />
        {error && <span className={styles.errorText}>{error}</span>}
      </div>
    </div>
  );
};
